import React from 'react';
import chain from 'lodash/chain';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import utc from 'dayjs/plugin/utc';
import { ThumbsUp, ThumbsDown, ArrowRight, HelpCircle } from 'lucide-react';
import { toast, ToastOptions } from 'react-toastify';

import { MESSAGE_TIMEOUT } from '../constants';

dayjs.extend(utc);
dayjs.extend(localizedFormat);

export const getDaysAgoDateString = (d: number): string =>
  dayjs
    .utc()
    .add(Math.round(d * 24) * -1, 'hour')
    .format();

export const getUrls = (text: string): string[] => text.match(/(https?:\/\/[^\s]+)/g) || [];

export const checkImageURL = (url: string): boolean => url.match(/\.(jpeg|jpg|gif|png)$/) != null;

export const parseImgurURL = (url: string): string => {
  const { origin, protocol, pathname } = new URL(url);
  if (origin === 'https://imgur.com' && protocol === 'https:') {
    return `https://i.imgur.com${pathname}.jpg`;
  }
  return url;
};

export const formatDatetime = (d: string | Date): string => dayjs.utc(d).format('YYYY-MM-DD');

export const formatTimestamp = (t: string | Date | number, isMobile?: boolean): string => 
  isMobile ? dayjs.utc(t).format('MM/DD HH:mm') : dayjs.utc(t).format('llll');

export const formatTimestampWithTimezone = (t: string | Date | number): string => dayjs(t).format('llll');

export const formatNumber = (num: number): string => {
  const si = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'G' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' },
  ];
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
  let i;
  for (i = si.length - 1; i > 0; i -= 1) {
    if (num >= si[i].value) {
      break;
    }
  }
  return (num / si[i].value).toFixed(1).replace(rx, '$1') + si[i].symbol;
};

export const getPushIcon = (tag: string, size?: 'sm' | 'md' | 'lg'): React.ReactElement => {
  const sizeClass = size === 'sm' ? 'h-4 w-4' : size === 'lg' ? 'h-6 w-6' : 'h-5 w-5';
  
  if (tag.includes('推')) {
    return <ThumbsUp className={`${sizeClass} text-blue-600`} />;
  }
  if (tag.includes('噓')) {
    return <ThumbsDown className={`${sizeClass} text-red-600`} />;
  }
  if (tag.includes('→')) {
    return <ArrowRight className={`${sizeClass} text-yellow-600`} />;
  }
  return <HelpCircle className={sizeClass} />;
};

export const groupByKey = <T,>(data: T[], key: keyof T): Record<string, T[]> => 
  chain(data).groupBy(key).value();

export const sortByDate =
  (order: number = 1) =>
  (a: string | Date, b: string | Date): number =>
    order > 0 ? new Date(b).getTime() - new Date(a).getTime() : new Date(a).getTime() - new Date(b).getTime();

export const showMessage = {
  info: (text: string) =>
    toast(text, {
      position: 'bottom-right',
      type: 'info',
    }),
  warn: (text: string) =>
    toast(text, {
      position: 'bottom-right',
      type: 'warning',
    }),
  success: (text: string) =>
    toast(text, {
      position: 'bottom-right',
      type: 'success',
    }),
  error: (text: string) =>
    toast(text, {
      position: 'bottom-right',
      type: 'error',
    }),
};

export const isNumeric = (n: any): boolean =>
  !Number.isNaN(parseFloat(n)) && Number.isFinite(Number(n));

export const truncString = (str: string, n: number): string =>
  str.length > n ? `${str.substr(0, n - 1)}...` : str;

export const TwentyFourHourToTwelveHourFormat = (h: any): string => {
  if (!isNumeric(h)) {
    return 'N/A';
  }

  const hourInNumber = parseInt(h, 10);

  if (hourInNumber < 0 || hourInNumber > 23) {
    return 'N/A';
  }

  if (hourInNumber / 12 === 1) {
    return '12pm';
  }

  if (hourInNumber === 0) {
    return '12am';
  }

  return hourInNumber > 12 ? `${hourInNumber - 12}pm` : `${hourInNumber}am`;
};

export const encodeHTML = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');